/**
 * BaigLabs logged-in home — parent product launchpad, not an ops stats wall.
 */
import React, { useEffect } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import {
  AccountTree as FlowIcon,
  Apps as AppsIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import FullscreenButton from '../components/FullscreenButton';
import { openBaiglabsProduct, productsForSurface } from '../config/baiglabsProducts';

const FONT_LINK_ID = 'baiglabs-parent-fonts';
const DISPLAY = '"Fraunces", Georgia, serif';
const BODY = '"Source Sans 3", "Source Sans Pro", system-ui, sans-serif';
const INK = '#0c1a2e';
const ACCENT = '#1a6bb5';

const STUDIO_SHORTCUTS = [
  { label: 'Chat', path: '/home', icon: ChatIcon, desc: 'Talk with your assistants' },
  { label: 'Studio', path: '/studio', icon: FlowIcon, desc: 'Flows, apps, and forms' },
  { label: 'Apps', path: '/studio/apps', icon: AppsIcon, desc: 'Studio app builder' },
];

function ensureFonts() {
  if (typeof document === 'undefined' || document.getElementById(FONT_LINK_ID)) return;
  const link = document.createElement('link');
  link.id = FONT_LINK_ID;
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,700&family=Source+Sans+3:wght@400;600;700&display=swap';
  document.head.appendChild(link);
}

function BaigLabsLaunchpad() {
  const navigate = useNavigate();
  const products = productsForSurface({ signedIn: true }).filter((p) => p.id !== 'studio');

  useEffect(() => {
    ensureFonts();
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100%',
        fontFamily: BODY,
        color: INK,
        background:
          'radial-gradient(900px 420px at 8% 0%, rgba(26, 107, 181, 0.14), transparent 55%),'
          + 'linear-gradient(180deg, #f4f7fb 0%, #eef3f8 100%)',
        p: { xs: 2, md: 3 },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, gap: 2 }}>
        <Box>
          <Typography
            component="h1"
            sx={{
              fontFamily: DISPLAY,
              fontWeight: 700,
              fontSize: { xs: '1.75rem', md: '2.15rem' },
              letterSpacing: '-0.02em',
              mb: 0.75,
            }}
          >
            Welcome to BaigLabs
          </Typography>
          <Typography sx={{ color: 'rgba(12, 26, 46, 0.68)', maxWidth: 520, lineHeight: 1.5 }}>
            Open a product below, or jump into Studio tools for chat, flows, and apps.
          </Typography>
        </Box>
        <FullscreenButton />
      </Box>

      <Typography
        component="h2"
        sx={{
          fontFamily: DISPLAY,
          fontWeight: 600,
          fontSize: '1.25rem',
          letterSpacing: '-0.02em',
          mb: 1.5,
        }}
      >
        Products
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
          gap: 1.75,
          mb: 4,
        }}
      >
        {products.map((product) => (
          <Box
            key={product.id}
            component="button"
            type="button"
            data-testid={`baiglabs-launch-${product.id}`}
            onClick={() => openBaiglabsProduct(product, navigate)}
            sx={{
              textAlign: 'left',
              cursor: 'pointer',
              border: '1px solid rgba(12, 26, 46, 0.1)',
              borderRadius: 2,
              bgcolor: 'rgba(255, 255, 255, 0.92)',
              p: 2.25,
              fontFamily: BODY,
              color: INK,
              transition: 'transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                borderColor: ACCENT,
                boxShadow: '0 8px 24px rgba(12, 26, 46, 0.08)',
              },
              '&:focus-visible': {
                outline: `2px solid ${ACCENT}`,
                outlineOffset: 2,
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: DISPLAY,
                fontWeight: 600,
                fontSize: '1.1rem',
                letterSpacing: '-0.02em',
                mb: 0.5,
              }}
            >
              {product.name}
            </Typography>
            <Typography sx={{ fontSize: '0.9rem', color: 'rgba(12, 26, 46, 0.65)', lineHeight: 1.45 }}>
              {product.tagline}
            </Typography>
          </Box>
        ))}
      </Box>

      <Typography
        component="h2"
        sx={{
          fontFamily: DISPLAY,
          fontWeight: 600,
          fontSize: '1.25rem',
          letterSpacing: '-0.02em',
          mb: 1.5,
        }}
      >
        Studio
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} useFlexGap flexWrap="wrap">
        {STUDIO_SHORTCUTS.map(({ label, path, icon: Icon, desc }) => (
          <Button
            key={path}
            onClick={() => navigate(path)}
            data-testid={`baiglabs-studio-${path.replace(/\//g, '') || 'home'}`}
            startIcon={<Icon />}
            variant="outlined"
            sx={{
              fontFamily: BODY,
              fontWeight: 600,
              textTransform: 'none',
              justifyContent: 'flex-start',
              borderColor: 'rgba(12, 26, 46, 0.16)',
              color: INK,
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              px: 2,
              py: 1.25,
              minWidth: { sm: 200 },
              '&:hover': {
                borderColor: ACCENT,
                bgcolor: 'rgba(26, 107, 181, 0.06)',
              },
            }}
          >
            <Box sx={{ textAlign: 'left' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.2 }}>{label}</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: 'rgba(12, 26, 46, 0.55)', fontWeight: 400 }}>
                {desc}
              </Typography>
            </Box>
          </Button>
        ))}
      </Stack>
    </Box>
  );
}

export default BaigLabsLaunchpad;
